import type { DeviceDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

export type DeviceDisableMode = "ingestion" | "visualization";

export type DeviceDisableState = {
  mode: DeviceDisableMode;
  disabledAt: Date | null;
};

function asJsonObject(value: unknown): JsonObject {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {};
}

function parseDisabledAt(value: unknown): Date | null {
  if (typeof value === "string" && value.trim()) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function readDeviceDisableState(device: DeviceDto | null | undefined): DeviceDisableState | null {
  if (!device) {
    return null;
  }
  const metadata = asJsonObject(device.metadata);
  const state = asJsonObject(metadata.disable_state);
  const legacyMode = typeof metadata.disabled_mode === "string" ? metadata.disabled_mode.trim().toLowerCase() : "";

  const modeValue =
    typeof state.mode === "string"
      ? state.mode.trim().toLowerCase()
      : legacyMode || (metadata.disabled === true ? "ingestion" : "");
  if (modeValue !== "ingestion" && modeValue !== "visualization") {
    return null;
  }

  return {
    mode: modeValue,
    disabledAt: parseDisabledAt(state.disabled_at ?? metadata.disabled_at),
  };
}

export function isDeviceIngestionDisabled(device: DeviceDto | null | undefined): boolean {
  return readDeviceDisableState(device)?.mode === "ingestion";
}

export function isDeviceVisualizationDisabled(device: DeviceDto | null | undefined): boolean {
  return readDeviceDisableState(device)?.mode === "visualization";
}

